import React, { FC, Fragment } from "react";
import { OnboardingStep, StepId, startsAfterArrival } from "./onboardingSteps";
import { useReservation } from "./reservationContext";
import { OnboardingState } from "./panels/types";
import { useLocale } from "../i18n/locale";

export interface OnboardingStepRailProps {
  steps: OnboardingStep[];
  activeId: StepId;
  /** Drives the per-step captions: "Both documents signed" and friends. */
  state: OnboardingState;
  onSelect: (id: StepId) => void;
  /**
   * A finished journey is a record, not a form. Every step stays visible and
   * ticked, but none of them can be opened again — there is nothing left to
   * change, and letting a tenant walk back into a completed step invites them
   * to try.
   */
  locked?: boolean;
  className?: string;
}

type StepState = "done" | "current" | "upcoming";

/**
 * Whether the work behind a step is finished.
 *
 * Two different questions, depending on the step. The ones that collect
 * something — the documents, the inventory, the certificate — are done when
 * that thing exists. The ones that are purely a passage are done once the
 * tenant has moved past them.
 */
const isStepDone = (
  step: OnboardingStep,
  index: number,
  activeIndex: number,
  state: OnboardingState
): boolean => {
  switch (step.id) {
    case "welcome":
      // The stay was confirmed before the tenant ever opened the link.
      return true;
    case "contract":
      return state.documentsSigned;
    case "inventory":
      return state.inventorySigned;
    case "insurance":
      return Boolean(state.insuranceFile) || state.insuranceSent;
    case "home":
      return index === activeIndex;
    default:
      return activeIndex > index;
  }
};

/**
 * The dark left column: who the tenant is, where they are in the journey, and
 * the before/after-arrival split.
 *
 * Every step except Welcome is clickable — Welcome has nothing behind it, and
 * the reference disables it for that reason. Gating the rest on real progress
 * is the DMS's call, not the layout's.
 *
 * On a phone the step list is replaced by a row of segments under the eyebrow:
 * a rail the tenant has to scroll past is worse than no rail, but no progress
 * indicator at all breaks the specification and leaves them unable to tell how
 * far through they are.
 */
const OnboardingStepRail: FC<OnboardingStepRailProps> = ({
  steps,
  activeId,
  state,
  onSelect,
  locked = false,
  className = "",
}) => {
  const { t } = useLocale();
  const reservation = useReservation();
  const activeIndex = steps.findIndex((step) => step.id === activeId);

  return (
    <aside
      className={`flex flex-col justify-between bg-navy px-6 py-7 text-white min-[760px]:px-[34px] min-[760px]:py-12 min-[1100px]:px-[52px] min-[1100px]:pb-[38px] min-[1100px]:pt-[58px] ${className}`}
    >
      <div>
        <p className="text-[11px] font-extrabold uppercase tracking-[0.19em] text-gold-light">
          {t.desktop.eyebrow}
        </p>

        {/* The phone's progress indicator.
            The rail below is hidden under 760px — seven steps in a navy column
            is not a phone layout — but the specification requires a progress
            indicator on every screen, and without one the tenant cannot tell
            where they are or return to a step they have finished. Segments
            rather than the full list, so it fits above the fold. */}
        <nav aria-label={t.desktop.eyebrow} className="mt-4 min-[760px]:hidden">
          <p className="text-[13px] leading-snug text-white">
            <span className="font-semibold">
              {t.desktop.stepCounter(activeIndex + 1, steps.length)}
            </span>
            <span className="text-[#bec8d3]">
              {" · "}
              {t.desktop.steps[activeId]?.rail}
            </span>
          </p>

          <ol className="mt-2 flex items-center gap-1.5">
            {steps.map((step, i) => {
              const done = isStepDone(step, i, activeIndex, state);
              const isCurrent = i === activeIndex;
              // A step the tenant has not reached yet is not somewhere to
              // jump to. Welcome follows the same rule as the rest now that it
              // is a screen they land on rather than an automatic tick.
              const reachable = !locked && (done || isCurrent);

              return (
                <li key={step.id} className="flex-1">
                  <button
                    type="button"
                    disabled={!reachable}
                    onClick={() => reachable && onSelect(step.id)}
                    aria-current={isCurrent ? "step" : undefined}
                    aria-label={`${t.desktop.stepCounter(i + 1, steps.length)} · ${
                      t.desktop.steps[step.id]?.rail
                    }`}
                    // The bar is 3px, the button 23px: a 3px tap target is
                    // unusable, so the padding carries the touch area and the
                    // bar carries the appearance.
                    className="block w-full py-[10px] focus:outline-none focus-visible:ring-2 focus-visible:ring-gold disabled:cursor-default"
                  >
                    <span
                      className={`block h-[3px] w-full rounded-full transition-colors ${
                        isCurrent ? "bg-white" : done ? "bg-gold" : "bg-[#41586f]"
                      }`}
                    />
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>

        <h1 className="mt-[18px] hidden font-serif text-[42px] font-normal leading-[1.08] tracking-[-0.02em] text-white min-[760px]:block">
          {/* The break is deliberate: the greeting reads as two lines in the
              design, name on its own. */}
          {t.desktop.greeting1}
          <br />
          {t.desktop.greeting2(reservation.tenantFirstName)}
        </h1>

        <p className="mt-[17px] hidden max-w-[310px] text-[14px] leading-[1.7] text-[#bec8d3] min-[760px]:block">
          {t.desktop.intro}
        </p>
      </div>

      <nav aria-label={t.desktop.eyebrow} className="hidden min-[760px]:block">
        <ol className="my-5">
          {steps.map((step, i) => {
            const copy = t.desktop.steps[step.id];
            const done = isStepDone(step, i, activeIndex, state);
            const stepState: StepState =
              i === activeIndex ? "current" : done ? "done" : "upcoming";
            const isLast = i === steps.length - 1;

            // Done and current are independent on the marker, which is why it
            // does not read off stepState. The fill says the work behind the
            // step exists; the ring says this is where the tenant is standing.
            // A step that is both — the last one always, any revisited step
            // the tenant has finished — wears the gold fill inside the white
            // ring, and drops the check: the ring already speaks for it.
            const isCurrent = stepState === "current";
            // Same rule as the phone segments above: a step the tenant has not
            // reached is not somewhere to jump to. Without it the rail walked
            // them straight past the contract into the closing steps, whatever
            // the Continue button said.
            const reachable = !locked && (done || isCurrent);
            const marker = done
              ? isCurrent
                ? "border-2 border-white bg-gold text-white shadow-[0_0_0_4px_#bd996142]"
                : "border border-gold bg-gold text-white"
              : isCurrent
              ? "border-2 border-white shadow-[0_0_0_4px_#bd996142]"
              : "border border-[#61748a]";

            return (
              <Fragment key={step.id}>
                {startsAfterArrival(steps, i) && (
                  // The group break. Indented to the titles, not the markers,
                  // so it reads as a heading over them rather than a step.
                  <li className="relative pb-1 pl-[45px]">
                    {i > 0 && (
                      // The connector runs behind the label as well: the break
                      // names the phase, it does not interrupt the journey.
                      <span
                        className="absolute inset-y-0 left-[13px] border-l border-[#466079]"
                        aria-hidden="true"
                      />
                    )}
                    <span className="text-[11px] min-[760px]:text-[7px] font-extrabold uppercase tracking-[0.15em] text-[#d1b074]">
                      {t.desktop.afterArrival}
                    </span>
                  </li>
                )}

                <li className="relative flex min-h-[62px] gap-[17px]">
                  {!isLast && (
                    // Bottom-anchored rather than a fixed height, so the line
                    // still reaches the next marker when a caption wraps.
                    <span
                      className="absolute bottom-0 left-[13px] top-[30px] border-l border-[#466079]"
                      aria-hidden="true"
                    />
                  )}

                  <button
                    type="button"
                    onClick={() => reachable && onSelect(step.id)}
                    disabled={!reachable}
                    aria-current={stepState === "current" ? "step" : undefined}
                    className="flex w-full gap-[17px] text-left disabled:cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-gold"
                  >
                    <span
                      className={`grid h-7 w-7 flex-none place-items-center rounded-full text-[12px] ${marker}`}
                      aria-hidden="true"
                    >
                      {done && !isCurrent ? "✓" : ""}
                    </span>

                    <span>
                      <strong
                        className={`block text-[13px] font-bold ${
                          stepState === "current"
                            ? "text-white"
                            : stepState === "done"
                            ? "text-[#d1dae2]"
                            : "text-[#9faebe]"
                        }`}
                      >
                        {copy.rail}
                      </strong>
                      <small
                        className={`block text-[11px] min-[760px]:text-[10px] ${
                          stepState === "current"
                            ? "text-[#d4b678]"
                            : "text-[#778b9f]"
                        }`}
                      >
                        {done
                          ? copy.doneCaption
                          : stepState === "current"
                          ? copy.currentCaption
                          : copy.caption}
                      </small>
                    </span>
                  </button>
                </li>
              </Fragment>
            );
          })}
        </ol>
      </nav>

      <div className="hidden gap-[13px] border-t border-[#38506a] pt-6 text-[11px] leading-[1.55] text-[#9eafbf] min-[760px]:flex">
        <span className="text-[23px] leading-none text-gold" aria-hidden="true">
          ◇
        </span>
        <p className="m-0">
          <strong className="text-[#d8e0e7]">{t.desktop.privateTitle}</strong>
          <br />
          {t.desktop.privateBody}
        </p>
      </div>
    </aside>
  );
};

export default OnboardingStepRail;
